import type {
  AnswerForType,
  DatabaseRecord,
  Enduser,
  FormFieldOptions,
  FormResponseValue,
} from "@tellescope/types-models";
import { useMemo } from "react";
import { SupportedSession, TellescopeModelFromName } from "@tellescope/tanstack-query";
import { convertFormFieldType } from "../utils/convert-form-field-type";
import { responsesSatisfyConditions } from "../utils";
import type { NextFieldLogicOptions } from "../utils/types";
import type { FormTypesWithValues, TellescopeFieldResponse } from "../types";
import { FieldDescription } from "../FieldDescription";
import { FieldFeedback } from "../FieldFeedback";
import { FieldTitleFontSizeWrapper } from "../FieldTitleFontSizeWrapper";
import { QuestionGroupSubField } from "./QuestionGroupSubField";
import { QuestionGroupRedirectSubField } from "./QuestionGroupRedirectSubField";
import type { ContinueToFormInApp } from "../useResolveRedirectTarget";
import { cn } from "@tellescope/ui";
import {
  PASS_THROUGH_FIELD_TEXT,
  type InterpolateFieldText,
} from "../hooks/use-tellescope-form-core/use-interpolate-field-text";

export interface QuestionGroupFieldProps {
  id: string;
  title?: string;
  options?: FormFieldOptions;
  className?: string;
  allFields: TellescopeModelFromName<"form_fields">[];
  currentResponses: Record<string, TellescopeFieldResponse>;
  getFieldProps: <T extends FormTypesWithValues>(
    fieldId: string,
    type: T
  ) => {
    id: string;
    type: T;
    title: string;
    description?: string;
    value: AnswerForType[T];
    onChange: (newValue: AnswerForType[T]) => void;
    onBlur?: () => void;
    error: string | null;
    touched: boolean;
    options?: FormFieldOptions;
  } | null;
  logicOptions: NextFieldLogicOptions;
  interpolateFieldText?: InterpolateFieldText;
  enduser?: TellescopeModelFromName<"endusers"> | Enduser;
  session?: SupportedSession | null;
  form?: TellescopeModelFromName<"forms">;
  formResponses?: FormResponseValue[];
  enduserId?: string;
  organizationSubdomain?: string;
  description?: string;
  htmlDescription?: string;
  applyDatabasePrepopulation?: (records: DatabaseRecord[]) => void;
  setCustomerId?: (customerId: string) => void;
  setBookedAppointmentUrl?: (bookedAppointmentUrl: string | undefined) => void;
  submitForm?: () => Promise<unknown>;
  isResponseSubmitted?: boolean;
  formResponseId?: string;
  rootResponseId?: string;
  documentGroupBackUrl?: string;
  continueToFormInApp?: ContinueToFormInApp;
  goToPreviousField?: () => void;
  isPreviousDisabled?: boolean;
}

/**
 * Renders a Question Group field — a container that displays multiple sub-fields
 * together as a single logical unit. Sub-fields are rendered inline and each
 * manages its own value/response independently.
 *
 * Conditional visibility of individual sub-fields is supported via `groupShowCondition`.
 */
export const QuestionGroupField = ({
  title,
  options,
  className,
  allFields,
  currentResponses,
  getFieldProps,
  logicOptions,
  interpolateFieldText = PASS_THROUGH_FIELD_TEXT,
  enduser,
  session,
  form,
  formResponses,
  enduserId,
  organizationSubdomain,
  description,
  htmlDescription,
  applyDatabasePrepopulation,
  setCustomerId,
  setBookedAppointmentUrl,
  submitForm,
  isResponseSubmitted,
  formResponseId,
  rootResponseId,
  documentGroupBackUrl,
  continueToFormInApp,
  goToPreviousField,
  isPreviousDisabled,
}: QuestionGroupFieldProps) => {
  const responsesArray = useMemo(() => Object.values(currentResponses), [currentResponses]);
  const subFieldDefs = options?.subFields ?? [];

  return (
    <div className={cn("space-y-4", className)}>
      <div className="space-y-2">
        {!!title && <h4 className="text-xl font-bold">{title}</h4>}
        <FieldDescription description={description} htmlDescription={htmlDescription} />
      </div>
      <div
        data-testid="question-group-sub-fields"
        className={cn("flex flex-col", options?.groupPadding === undefined && "gap-8")}
        style={options?.groupPadding === undefined ? undefined : { gap: `${options.groupPadding}px` }}
      >
        {subFieldDefs.map(({ id: subFieldId }) => {
          const subField = allFields.find((f) => f.id === subFieldId);
          if (!subField) return null;

          if (subField.groupShowCondition && Object.keys(subField.groupShowCondition).length) {
            const isVisible = responsesSatisfyConditions(responsesArray, subField.groupShowCondition, logicOptions);
            if (!isVisible) return null;
          }

          const convertedType = convertFormFieldType(subField.type);
          if (convertedType === "Question Group") return null;
          if (convertedType === null) {
            if (subField.type !== "description") return null;
            return (
              <div key={subFieldId} className="space-y-1">
                {!!subField.title && <p className="text-sm">{interpolateFieldText.text(subField.title)}</p>}
                <FieldDescription
                  description={interpolateFieldText.text(subField.description) || undefined}
                  htmlDescription={interpolateFieldText.html(subField.htmlDescription) || undefined}
                />
              </div>
            );
          }

          if (convertedType === "Redirect") {
            return (
              <QuestionGroupRedirectSubField
                key={subFieldId}
                subField={subField}
                getFieldProps={getFieldProps}
                responsesArray={responsesArray}
                isStaffSession={session?.type === "user"}
                enduser={enduser}
                enduserId={enduserId}
                fieldsArr={allFields}
                submitForm={submitForm}
                isResponseSubmitted={isResponseSubmitted}
                formResponseId={formResponseId}
                rootResponseId={rootResponseId}
                documentGroupBackUrl={documentGroupBackUrl}
                continueToFormInApp={continueToFormInApp}
              />
            );
          }

          return (
            <FieldTitleFontSizeWrapper key={subFieldId} titleFontSize={subField.titleFontSize}>
              <div className="space-y-1">
                <QuestionGroupSubField
                  subField={subField}
                  convertedType={convertedType}
                  getFieldProps={getFieldProps}
                  responsesArray={responsesArray}
                  logicOptions={logicOptions}
                  enduser={enduser}
                  session={session}
                  form={form}
                  formResponses={formResponses}
                  enduserId={enduserId}
                  organizationSubdomain={organizationSubdomain}
                  applyDatabasePrepopulation={applyDatabasePrepopulation}
                  setCustomerId={setCustomerId}
                  setBookedAppointmentUrl={setBookedAppointmentUrl}
                  formResponseId={formResponseId}
                  goToPreviousField={goToPreviousField}
                  isPreviousDisabled={isPreviousDisabled}
                />
                <FieldFeedback feedback={subField.feedback} value={currentResponses[subFieldId]?.answer.value} />
              </div>
            </FieldTitleFontSizeWrapper>
          );
        })}
      </div>
    </div>
  );
};
